import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    API_GATEWAY_PATH,
    ILeasingCarExtraOptionsResponse,
    ILeasingCarOptionsRequest,
    ILeasingCarOptionsResponse,
    ILeasingCarPriceRequest,
    ILeasingColorDescriptionsRequest,
    ILeasingColorDescriptionsResponse,
    ILeasingCreateLeasingOrderRequest,
    ILeasingExtraOptionsRequest,
    ILeasingGetCarListRequest,
    ILeasingGetCarListResponse,
    ILeasingOptionDescriptionsRequest,
    ILeasingOptionDescriptionsResponse,
    ILeasingPickupDeliveryUpdateRequest,
    ILeasingPlanUpdateRequest,
    ILeasingStepInfoUpdateRequest,
    ILeasingUpdateExtraOptionsRequest,
    ILeasingUpdateOptionsRequest,
    ILeasingUsedCarIntegratedValuesRequest,
    ILeasingUsedCarIntegratedValuesResponse,
    ILeasingUsedCarPriceRequest,
    ILeasingUsedCarPriceResponse,
    ILeasingUsedCarValidIntervalsRequest,
    ILeasingUsedCarValidIntervalsResponse,
    ILeasingUserInfoUpdateRequest,
    ILeasingValidIntervalsRequest,
    ILeasingValidIntervalsResponse,
    Leasing_GetValidIntervalsRequest,
    Leasing_GetValidIntervalsResponse,
} from '@impact/data';
import { BehaviorSubject, EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, take, takeUntil } from 'rxjs/operators';
import { ContentService } from '../content/content.service';
import { roundToMultiple } from '../core/helpers';
import { LEASING_CHECKOUT_STEP } from './leasing-checkout-flow.service';

export interface IStoredActiveLeasingCheckout {
    // extends CheckoutCreatePurchaseOrderRequest - In time - should extend BE status for order/checkout
    purchaseOrderId?: string;
    currentStep?: LEASING_CHECKOUT_STEP;
}

export interface IIntervalRanges {
    highestMinKm?: number;
    lowestMaxKm?: number;
    highestMinMonthDuration?: number;
    lowestMaxMonthDuration?: number;
}

export enum VEHICLETYPES {
    P = 'Gule plader',
    V = 'Hvide plader'
}

export enum FUELTYPES {
    Diesel = 'D',
    Benzin = 'B'
}

export interface ILeasingOption {
    optionId: number;
    optionDescription: string;
    optionPrice: number;
}

export type LeasingAgreementType = 'F' | 'O';
export type LeasingPurpose = 'brugtbilerhverv' | 'brugtbilprivat';

export enum LEASING_AGREEMENT_TYPES {
    OPERATIONAL = 'operational',
    FINANCIAL = 'financial',
}

export enum LEASING_PURPOSE_TYPES {
    PRIVATE = 'brugtbilprivat',
    BUSINESS = 'brugtbilerhverv'
}

// These enum values are mirroring the values coming from Umbraco and BE endpoints.
// If either of those change, these need to be changed as well
export enum LEASING_PLAN_TYPES {
    TYPE_OPERATIONAL = 'O',
    TYPE_FINANCIAL = 'F',
    PURPOSE_PRIVATE = 'brugtbilprivat',
    PURPOSE_BUSINESS = 'brugtbilerhverv'
}

export interface IActiveLeasingView {
    leasingType?: string;
    leasingPurpose?: string;
}

export interface ILeasingConfiguratorData {
    downPayment?: number,
    monthlyPayment?: number,
    monthlyTerms?: number,
    yearlyDistanceInKm?: number,
    residualValue?: number,
    totalPrice?: number,
    selectedLeasingPlanType?: LEASING_AGREEMENT_TYPES,
    leasingtypeForPriceCalculation?: string,
    leasingType?: string,
    isLeasingForBusiness?: boolean,
    isLeasingTypeFinancial?: boolean
}

@Injectable({
    providedIn: 'root',
})
export class LeasingService {
    private cancelCreateOrder$ = new Subject<void>();
    private cancelLeasingCarList$ = new Subject<void>();
    private cancelLeasingIntegratedValues$ = new Subject<void>();
    private cancelLeasingOptions$ = new Subject<void>();
    
    private leasingData$ = this.contentService
        .getLeasingData()
        .pipe(shareReplay(1));

    private leasingPlanOptions$ = new BehaviorSubject<
        IActiveLeasingView | undefined
    >(undefined);

    currentUsedCarLeasingPrice$ = new BehaviorSubject<any>({});
    errorState$ = new BehaviorSubject(false);
    intervalRanges$ = new BehaviorSubject<IIntervalRanges>({});
    latestDurationSetting$ = new BehaviorSubject<number | undefined>(undefined);
    latestMilageSetting$ = new BehaviorSubject<number | undefined>(undefined);
    leasingConfigDataAndRanges$ = new BehaviorSubject<any>({});
    leasingPriceLoading$ = new BehaviorSubject<boolean>(false);

    constructor(
        private contentService: ContentService,
        private http: HttpClient,
    ) {}

    getLeasingGlobalData() {
        return this.leasingData$.pipe(take(1));
    }

    //
    // Loucon API

    // GET ENDPOINTS

    getLeasingVehicleIntervals(reqBody: Leasing_GetValidIntervalsRequest) {
        // If new intervals are called for, reset old settings.
        this.resetLeasingIntervalsSettings();

        // Fetch new
        return this.http.post<Leasing_GetValidIntervalsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetValidIntervals`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    getLeasingCarList(reqBody: ILeasingGetCarListRequest): Observable<ILeasingGetCarListResponse> {
        this.cancelLeasingCarList$.next();

        return this.http.post<ILeasingGetCarListResponse>(`/${API_GATEWAY_PATH}/Leasing/GetCarList`, reqBody)
            .pipe((takeUntil(this.cancelLeasingCarList$),
            catchError((err) => {
                return throwError(err);
            })
        ));
    }

    getLeasingOptionDescriptions(reqBody: ILeasingOptionDescriptionsRequest): Observable<ILeasingOptionDescriptionsResponse> {
        return this.http.post<ILeasingOptionDescriptionsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetOptionDescriptions`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    getLeasingColorDescriptions(reqBody: ILeasingColorDescriptionsRequest): Observable<ILeasingColorDescriptionsResponse> {
        return this.http.post<ILeasingColorDescriptionsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetColorDescriptions`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    // Leasing options: prices and states for all configurations
    getLeasingOptions(reqBody: ILeasingCarOptionsRequest): Observable<ILeasingCarOptionsResponse> {
        this.cancelLeasingOptions$.next();

        return this.http.post<ILeasingCarOptionsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetOptions`, reqBody)
            .pipe((takeUntil(this.cancelLeasingOptions$),
            catchError((err) => {
                return throwError(err);
            }))
        );
    }

    getLeasingExtraOptions(reqBody: ILeasingExtraOptionsRequest): Observable<ILeasingCarExtraOptionsResponse> {
        return this.http.post<ILeasingCarExtraOptionsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetExtraOptions`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    getLeasingCarPrice(reqBody: ILeasingCarPriceRequest): Observable<any> {
        return this.http.post<any>(`/${API_GATEWAY_PATH}/Leasing/GetCarPrice`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    getLeasingValidIntervals(reqBody: ILeasingValidIntervalsRequest): Observable<ILeasingValidIntervalsResponse> {
        return this.http.post<ILeasingValidIntervalsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetValidIntervals`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    // Motorpoint: get leasing intervals and data
    getUsedCarLeasingIntegratedValues(reqBody: ILeasingUsedCarIntegratedValuesRequest): Observable<ILeasingUsedCarIntegratedValuesResponse> {
        this.cancelLeasingIntegratedValues$.next();
        return this.http.post<ILeasingUsedCarIntegratedValuesResponse>(`/${API_GATEWAY_PATH}/Leasing/GetUsedCarIntegratedValues`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            }),
            takeUntil(this.cancelLeasingIntegratedValues$)
        );
    }

    broadcastUsedCarLeasingPrice(price: any) {
        this.currentUsedCarLeasingPrice$.next(price);
    }

    // Create leasing
    createLeasingPurchaseOrder(reqBody: ILeasingCreateLeasingOrderRequest): Observable<any> {
        this.cancelCreateOrder$.next();
        return this.http.post<ILeasingCreateLeasingOrderRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/CreateLeasingPurchaseOrder`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            }),
            takeUntil(this.cancelCreateOrder$)
        );
    }

    getLeasingPurchaseOrder(id: string): Observable<any> {
        return this.http.get<any>(`/${API_GATEWAY_PATH}/LeasingCheckout/GetLeasingPurchaseOrder/${id}`).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    completeLeasingPurchaseOrder(id: string) {
        return this.http.post<any>(`/${API_GATEWAY_PATH}/LeasingCheckout/CompleteLeasingPurchaseOrder`, {
            purchaseOrderId: id
        }).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    //
    // UPDATE ENDPOINTS

    updateLeasingOptions(data: ILeasingUpdateOptionsRequest): Observable<any> {
        if (!data?.purchaseOrderId) {
            return EMPTY;
        }

        // Make sure that options is never null
        data.options = data.options ?? [];

        return this.http.post<ILeasingUpdateOptionsRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/UpdateLeasingOptions`, data).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    updateLeasingExtraOptions(data: ILeasingUpdateExtraOptionsRequest): Observable<any> {
        if (!data?.purchaseOrderId) {
            return EMPTY;
        }

        // Make sure that options is never null
        data.options = data.options ?? [];

        return this.http.post<ILeasingUpdateExtraOptionsRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/UpdateLeasingExtraOptions`, data).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    // Update LEASING AGREEMENT/PLAN
    updateLeasingData(leasingPlanData: ILeasingPlanUpdateRequest): Observable<any> {
        return this.http.post<ILeasingPlanUpdateRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/UpdateLeasingData`, leasingPlanData).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    updateLeasingPickupDelivery(reqBody: ILeasingPickupDeliveryUpdateRequest) {
        return this.http.post<ILeasingPlanUpdateRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/UpdateLeasingPickupDelivery`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    updateLeasingUserInfo(reqBody: ILeasingUserInfoUpdateRequest) {
        return this.http.post<ILeasingUserInfoUpdateRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/UpdateLeasingUserInfo`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    updateLeasingStepInfo(reqBody: ILeasingStepInfoUpdateRequest) {
        return this.http.post<ILeasingStepInfoUpdateRequest>(`/${API_GATEWAY_PATH}/LeasingCheckout/UpdateLeasingStepInfo`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    //
    // BN Checkout endpoints

    getUsedCarLeasingPrice(carData: any, leasingData: any): Observable<ILeasingUsedCarPriceResponse> {
        this.leasingPriceLoading$.next(true);

        const reqBody: ILeasingUsedCarPriceRequest = {
            carMilage: carData?.mileage?.data,
            downpayment: leasingData.downPayment,
            durationInMonths: leasingData.monthlyTerms,
            extraOptions: [0],
            firstRegistrationDate: String(carData.registrationDate?.data),
            fueltype: carData?.propellant?.data === 'Benzin' ? 'B' : carData?.propellant?.data === 'El' ? 'E' : 'D',
            km: leasingData.yearlyDistanceInKm,
            isLeasingTypeFinancial: leasingData.leasingType === true ? true : (leasingData.leasingType === LEASING_PLAN_TYPES.TYPE_FINANCIAL ? true : false),
            isLeasingForBusiness: leasingData.leasingtypeForPriceCalculation === true ? true :(leasingData.leasingtypeForPriceCalculation === LEASING_PLAN_TYPES.PURPOSE_BUSINESS ? true : false),
            make: carData.make?.data,
            model: carData.model?.data,
            modelDescription: carData.variant?.data,
            password: '',
            registrationFee: carData?.registrationFee?.data,
            steelPrice: carData?.steelPrice?.data,
            vehicleType: 'P',
        };

        return this.http.post<ILeasingUsedCarPriceResponse>(`/${API_GATEWAY_PATH}/Leasing/GetUsedCarPrice`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            }),
            finalize(() => this.leasingPriceLoading$.next(false))
        );
    }


    getUsedCarLeasingValidIntervals(reqBody: ILeasingUsedCarValidIntervalsRequest): Observable<ILeasingUsedCarValidIntervalsResponse> {
        return this.http.post<ILeasingUsedCarValidIntervalsResponse>(`/${API_GATEWAY_PATH}/Leasing/GetUsedCarValidIntervals`, reqBody).pipe(
            catchError((err) => {
                return throwError(err);
            })
        );
    }

    //
    // Helpers
    //

    // Toggle the view on leasing vehicles, will be broadcasted to product-item components
    setActiveLeasingToggles(leasingOptions: IActiveLeasingView): void {
        this.leasingPlanOptions$.next(leasingOptions);
    }

    getActiveLeasingToggles(): Observable<IActiveLeasingView | undefined> {
        return this.leasingPlanOptions$.asObservable();
    }

    broadcastUsedCarLeasingData(data: any) {
            this.leasingConfigDataAndRanges$.next(data);
    }

    resetLeasingIntervalsSettings() {
        if (this.intervalRanges$.value) {
            this.intervalRanges$.next({});
        }
    }

    setLatestMilageSetting(value: number) {
        if (isNaN(value)) { return; }
        this.latestMilageSetting$.next(value);
    }

    setLatestDurationSetting(value: number) {
        if (isNaN(value)) { return; }
        this.latestDurationSetting$.next(value);
    }

    createMonthsRange(start: number, end: number, activeNumber?: number) {
        const monthsInterval = 12;
        const arrayLength = Math.floor(((end - start) / monthsInterval)) + 1;
        const range = [...Array(arrayLength).keys()].map(x => (x * monthsInterval) + start);
        const monthsRange = [];
        const defaultValue: number = range.indexOf(36) !== -1 ? range[range.indexOf(36)] : range[range.length - 1];

        // Create Tickmark objects
        for (let i = 0; i < range.length; i++) {
            monthsRange.push({
                label: String(range[i]),
                isDefault: activeNumber ? range[i] === activeNumber : range[i] === defaultValue,
                value: range[i],
            });
        }

        return monthsRange;
    }

    createDownPaymentRange(leasingIntervalsData: any, fullPrice: number) {
        return {
            minValue: roundToMultiple(
                fullPrice * (leasingIntervalsData.minDownPaymentPct / 100),
                1000
            ),
            
            maxValue: roundToMultiple(
                fullPrice * (leasingIntervalsData.maxDownPaymentPct / 100),
                1000
            ),
        };
    }

    getDefaultYearlyDistanceInKmRange(range: number[]): number {
        let defaultValue = 15000;

        if (range[0] > defaultValue) {
            defaultValue = range[0];
        }

        return defaultValue;
    }

    getDefaultDownPayment(fullPrice: number) {
        return roundToMultiple(fullPrice * .07, 1000, 'ceil');
    }

    extractOptionIds(optionsArray: any[]) {
        if (optionsArray.length) {
            return optionsArray.map(option => option.optionId);
        } else {
            return [];
        }
    }
}
